import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { itemsAPI, exportAPI } from '../lib/api';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import RequireCap from '../components/auth/RequireCap';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../components/ui/select';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '../components/ui/table';
import { 
  Plus, 
  Search, 
  Package, 
  Image as ImageIcon,
  Filter,
  Grid,
  List,
  Laptop,
  Smartphone,
  Radio,
  Shirt,
  Download,
  ArrowUp,
  ArrowDown,
  ArrowUpDown
} from 'lucide-react';
import { formatCurrency } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_ICONS = {
  uniforms: Shirt,
  equipment: Package,
  it_assets: Laptop,
  safety: Package,
  accessories: Package,
};

const CATEGORY_LABELS = {
  uniforms: 'Uniforms',
  equipment: 'Equipment',
  it_assets: 'IT Assets',
  safety: 'Safety Gear',
  accessories: 'Accessories',
};

export default function Items() {
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [viewMode, setViewMode] = useState('list');
  const [sortKey, setSortKey] = useState('name');
  const [sortDir, setSortDir] = useState('asc');

  useEffect(() => {
    loadItems();
  }, []);

  const loadItems = async () => {
    try {
      const response = await itemsAPI.listAll({ is_active: true });
      setItems(response.data);
    } catch (error) {
      console.error('Failed to load items:', error);
    } finally {
      setLoading(false);
    }
  };

  const categories = [...new Set(items.map(item => item.category))];

  const filteredItems = items.filter(item => {
    const matchesSearch = item.name.toLowerCase().includes(search.toLowerCase()) ||
                         item.category.toLowerCase().includes(search.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || item.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const sortedItems = [...filteredItems].sort((a, b) => {
    let aVal, bVal;
    switch (sortKey) {
      case 'name': aVal = a.name.toLowerCase(); bVal = b.name.toLowerCase(); break;
      case 'category': aVal = a.category; bVal = b.category; break;
      case 'variants': aVal = a.variants?.length || 0; bVal = b.variants?.length || 0; break;
      case 'qty': aVal = (a.variants || []).reduce((s, v) => s + (v.quantity || 0), 0); bVal = (b.variants || []).reduce((s, v) => s + (v.quantity || 0), 0); break;
      case 'status': aVal = a.is_active ? 1 : 0; bVal = b.is_active ? 1 : 0; break;
      default: aVal = a.name.toLowerCase(); bVal = b.name.toLowerCase();
    }
    if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  const SortHeader = ({ label, colKey }) => (
    <TableHead className="cursor-pointer select-none hover:bg-slate-100 transition-colors" onClick={() => toggleSort(colKey)}>
      <div className="flex items-center gap-1">
        {label}
        {sortKey === colKey ? (sortDir === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />) : <ArrowUpDown className="w-3 h-3 text-slate-300" />}
      </div>
    </TableHead>
  );

  const ItemCard = ({ item }) => {
    const CategoryIcon = CATEGORY_ICONS[item.category] || Package;
    const prices = (item.variants || []).map(v => v.price || 0).filter(p => p > 0);
    const priceDisplay = prices.length > 0 
      ? prices.length === 1 || new Set(prices).size === 1 
        ? formatCurrency(prices[0]) 
        : `${formatCurrency(Math.min(...prices))} - ${formatCurrency(Math.max(...prices))}`
      : '-';
    return (
      <Card 
        className="card-hover cursor-pointer overflow-hidden"
        onClick={() => navigate(`/items/${item.id}`)}
        data-testid={`item-card-${item.id}`}
      >
        <div className="aspect-square bg-slate-100 relative">
          {item.primary_image ? (
            <img 
              src={`${API_URL}${item.primary_image.thumbnail_url || item.primary_image.url}`}
              alt={item.name}
              className="w-full h-full object-cover"
              loading="lazy"
              decoding="async"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <CategoryIcon className="w-16 h-16 text-slate-300" />
            </div>
          )}
          {item.category === 'it_assets' && (
            <Badge className="absolute top-2 left-2 bg-indigo-600">IT Asset</Badge>
          )}
        </div>
        <CardContent className="p-4">
          <h3 className="font-semibold text-slate-900 truncate">{item.name}</h3>
          <p className="text-sm text-slate-500">{CATEGORY_LABELS[item.category] || item.category}</p>
          <div className="flex items-center justify-between mt-3">
            <span className="font-medium text-slate-900">{priceDisplay}</span>
            <Badge variant="outline">{item.variants?.length || 0} variants</Badge>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-6" data-testid="items-page">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Asset Catalog</h1>
          <p className="text-slate-500 mt-1">
            Manage uniforms, equipment, and IT assets
          </p>
        </div>
        <div className="flex gap-2">
          <RequireCap module="assets" action="export">
            <Button variant="outline" onClick={async () => {
              try {
                const res = await exportAPI.assets();
                const url = window.URL.createObjectURL(new Blob([res.data]));
                const a = document.createElement('a'); a.href = url; a.download = 'all_assets.csv'; a.click();
                window.URL.revokeObjectURL(url);
              } catch (e) { toast.error('Export failed'); }
            }} data-testid="export-assets-btn">
              <Download className="w-4 h-4 mr-2" />Export CSV
            </Button>
          </RequireCap>
          <RequireCap module="assets" action="add">
            <Button onClick={() => navigate('/items/new')} data-testid="add-item-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Item
            </Button>
          </RequireCap>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input 
                placeholder="Search items..." 
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="items-search"
              />
            </div>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-full md:w-48" data-testid="category-filter">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                <SelectItem value="uniforms">Uniforms</SelectItem>
                <SelectItem value="equipment">Equipment</SelectItem>
                <SelectItem value="it_assets">IT Assets</SelectItem>
                <SelectItem value="safety">Safety Gear</SelectItem>
                <SelectItem value="accessories">Accessories</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex gap-1 border rounded-md p-1">
              <Button 
                variant={viewMode === 'grid' ? 'default' : 'ghost'} 
                size="sm"
                onClick={() => setViewMode('grid')}
              >
                <Grid className="w-4 h-4" />
              </Button>
              <Button 
                variant={viewMode === 'list' ? 'default' : 'ghost'} 
                size="sm"
                onClick={() => setViewMode('list')}
              >
                <List className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Items */}
      {loading ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="h-72 skeleton-pulse rounded-lg"></div>
          ))}
        </div>
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
          {filteredItems.map(item => (
            <ItemCard key={item.id} item={item} />
          ))}
          {filteredItems.length === 0 && (
            <div className="col-span-full text-center py-12 text-slate-500">
              No items found matching your criteria
            </div>
          )}
        </div>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-16">Image</TableHead>
                <SortHeader label="Name" colKey="name" />
                <SortHeader label="Category" colKey="category" />
                <SortHeader label="Variants" colKey="variants" />
                <SortHeader label="Total Qty" colKey="qty" />
                <SortHeader label="Status" colKey="status" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedItems.map(item => {
                const totalQty = (item.variants || []).reduce((sum, v) => sum + (v.quantity || 0), 0);
                return (
                <TableRow 
                  key={item.id} 
                  className="cursor-pointer"
                  onClick={() => navigate(`/items/${item.id}`)}
                  data-testid={`item-row-${item.id}`}
                >
                  <TableCell>
                    <div className="w-12 h-12 rounded bg-slate-100 overflow-hidden">
                      {item.primary_image ? (
                        <img 
                          src={`${API_URL}${item.primary_image.thumbnail_url || item.primary_image.url}`}
                          alt={item.name}
                          className="w-full h-full object-cover"
                          loading="lazy"
                          decoding="async"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Package className="w-6 h-6 text-slate-300" />
                        </div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="font-medium">{item.name}</TableCell>
                  <TableCell>{CATEGORY_LABELS[item.category] || item.category}</TableCell>
                  <TableCell>{item.variants?.length || 0}</TableCell>
                  <TableCell className="font-medium">{totalQty}</TableCell>
                  <TableCell>
                    <Badge variant={item.is_active ? 'default' : 'secondary'}>
                      {item.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </TableCell>
                </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
}
